import { getApiClient } from '@apps/web/lib/api_client';
import {
    type Mode,
    type SelectionBatchItemRequest,
    type SelectionBatchItemResult,
    type SelectionBatchResponse,
} from '@libraries/typescript/api_client/schema_types';

export type RecommendationUnitType = 'shift' | 'shift_group';

const SELECTION_BATCH_MAX_ITEMS = 100;

export type BatchUpdateRecommendationCaregiverSelectionsParams = {
    readonly organizationId: string;
    readonly outreachId: string;
    readonly unitType: RecommendationUnitType;
    readonly unitId: string;
    /** Narrows a group unit to these member shifts; omit for the whole unit. */
    readonly shiftIds?: ReadonlyArray<string>;
    readonly items: ReadonlyArray<SelectionBatchItemRequest>;
    // The v3 chat card carries its harness scope's mode rather than
    // inheriting whatever the page store happens to hold.
    readonly mode?: Mode;
    readonly reason?: string;
};

/**
 * Apply every checkbox change from one click in a single request.
 *
 * A show-all page holds at most `SELECTION_BATCH_MAX_ITEMS` rows, so one
 * chunk is the normal case; a selected-mode page or cross-page selection can
 * be larger and is split into sequential chunks because the endpoint rejects
 * a larger batch.
 *
 * This lives outside `swr_data_fetching` so the v3 chat slice can call the
 * one selection writer without importing that module's feature graph.
 */
export async function batchUpdateRecommendationCaregiverSelections({
    organizationId,
    outreachId,
    unitType,
    unitId,
    shiftIds,
    items,
    mode,
    reason,
}: BatchUpdateRecommendationCaregiverSelectionsParams): Promise<SelectionBatchResponse> {
    const results: Array<SelectionBatchItemResult> = [];
    for (
        let start = 0;
        start < items.length;
        start += SELECTION_BATCH_MAX_ITEMS
    ) {
        const response = await getApiClient().post({
            url: '/organizations/{organization_id}/caregiver-recommendations/selections/batch',
            pathParams: { organization_id: organizationId },
            ...(mode === undefined ? {} : { mode }),
            ...(reason === undefined ? {} : { reason }),
            body: {
                outreach_id: outreachId,
                unit_type: unitType,
                unit_id: unitId,
                ...(shiftIds === undefined ? {} : { shift_ids: [...shiftIds] }),
                items: items.slice(start, start + SELECTION_BATCH_MAX_ITEMS),
            },
            idempotent: true,
        });
        results.push(...response.items);
    }
    return { outreach_id: outreachId, items: results };
}
